import React from 'react';
import LegalLayout, { LegalSection } from '../components/LegalLayout';

export default function CGU() {
  return (
    <LegalLayout title="Conditions Générales d'Utilisation" lastUpdated="1er janvier 2025">
      <p className="text-[#a0a0ab]">
        Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation de la plateforme Kurso. En vous inscrivant, vous acceptez sans réserve les présentes CGU.
      </p>

      <LegalSection title="Article 1 — Objet">
        <p>
          Kurso est une plateforme d'apprentissage en ligne qui génère des cours pédagogiques structurés grâce à l'intelligence artificielle. Les présentes CGU définissent les droits et obligations des utilisateurs dans le cadre de l'utilisation de ces services.
        </p>
      </LegalSection>

      <LegalSection title="Article 2 — Acceptation des CGU">
        <p>
          L'utilisation de Kurso implique l'acceptation pleine et entière des présentes CGU. Kurso se réserve le droit de modifier les CGU à tout moment. Les utilisateurs seront informés de toute modification substantielle par email. La poursuite de l'utilisation après notification vaut acceptation des nouvelles CGU.
        </p>
      </LegalSection>

      <LegalSection title="Article 3 — Description des services">
        <p>Kurso propose les services suivants :</p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li>Génération de cours pédagogiques par IA sur tout sujet</li>
          <li>Quiz interactifs et suivi de progression</li>
          <li>Système de gamification (XP, badges, série quotidienne)</li>
          <li>Bilan de compétences Kurso</li>
          <li>Espaces collaboratifs Famille et Organisation</li>
          <li>Tableaux de bord administratifs</li>
        </ul>
        <p className="mt-2">
          Les contenus générés par l'IA sont fournis à titre éducatif uniquement et ne constituent pas un conseil professionnel (médical, juridique, financier, etc.).
        </p>
      </LegalSection>

      <LegalSection title="Article 4 — Inscription et compte">
        <p>Pour accéder aux services Kurso, vous devez :</p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li>Être âgé(e) d'au moins 13 ans (16 ans sans consentement parental)</li>
          <li>Fournir une adresse email valide et un mot de passe sécurisé</li>
          <li>Renseigner des informations exactes et à jour</li>
        </ul>
        <p className="mt-2">
          Vous êtes responsable de la confidentialité de vos identifiants. Toute activité effectuée depuis votre compte vous est attribuée. En cas de compromission, contactez-nous immédiatement à contact@mykurso.fr.
        </p>
        <p className="mt-2">
          Kurso se réserve le droit de suspendre ou supprimer tout compte en cas de violation des présentes CGU, d'utilisation frauduleuse ou d'activité préjudiciable à la plateforme.
        </p>
      </LegalSection>

      <LegalSection title="Article 5 — Plans d'abonnement et paiement">
        <div className="space-y-2">
          <div className="bg-[#20c997]/10 border border-[#20c997]/30 rounded-xl p-3">
            <p className="text-white text-xs font-medium">Plan Explorateur (Gratuit)</p>
            <p className="text-[#a0a0ab] text-xs">20 crédits offerts chaque mois, soit 1 cours IA complet par mois (6 modules, vidéos d'introduction avec voix-off IA, QCM), accès aux cours publics, sans carte bancaire</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-white text-xs font-medium">Plan Apprenant — 6,90 €/mois</p>
            <p className="text-[#a0a0ab] text-xs">200 crédits IA/mois, exports PDF & PPTX, suivi de progression</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-white text-xs font-medium">Plan Créateur Pro — 24,90 €/mois</p>
            <p className="text-[#a0a0ab] text-xs">600 crédits IA/mois, exports SCORM & xAPI, éditeur de cours avancé, accès marketplace</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-white text-xs font-medium">Plan Organisation — 69 €/mois</p>
            <p className="text-[#a0a0ab] text-xs">2 000 crédits IA partagés, gestion multi-membres, dashboard RH, tous les exports</p>
          </div>
          <div className="bg-white/5 rounded-xl p-3">
            <p className="text-white text-xs font-medium">Plan RH & GPEC — 149 €/mois</p>
            <p className="text-[#a0a0ab] text-xs">6 000 crédits IA partagés, gestion GPEC avancée, accès API, support prioritaire</p>
          </div>
        </div>
        <p className="mt-3">
          Un cours IA complet coûte 10 crédits. Les crédits sont débités au lancement de la génération et <strong>remboursés automatiquement</strong> si le cours n'atteint pas 100 % des critères de qualité (vidéo d'introduction, contenu et QCM pour chaque module) et n'est pas publié.
        </p>
        <p className="mt-3">
          Les paiements sont traités de manière sécurisée par <strong>Stripe</strong>. Les abonnements sont renouvelés automatiquement chaque mois. Les prix sont indiqués TTC.
        </p>
      </LegalSection>

      <LegalSection title="Article 6 — Droit de rétractation">
        <p>
          Conformément à l'article L221-28 du Code de la consommation, le droit de rétractation de 14 jours ne s'applique pas aux contenus numériques fournis immédiatement après l'achat, avec votre accord exprès.
        </p>
        <p className="mt-2">
          Cependant, Kurso vous offre une <strong>garantie satisfaction de 7 jours</strong> : si vous n'êtes pas satisfait(e) de votre premier mois, contactez-nous pour un remboursement intégral.
        </p>
      </LegalSection>

      <LegalSection title="Article 7 — Résiliation">
        <p>
          Vous pouvez résilier votre abonnement à tout moment depuis votre profil ou en contactant contact@mykurso.fr. La résiliation prend effet à la fin de la période d'abonnement en cours. Aucun remboursement proratisé ne sera effectué, sauf dans le cadre de la garantie satisfaction.
        </p>
        <p className="mt-2">
          En cas de résiliation, vos données d'apprentissage sont conservées pendant 3 ans puis supprimées, sauf demande expresse d'effacement immédiat.
        </p>
      </LegalSection>

      <LegalSection title="Article 8 — Propriété intellectuelle">
        <p>
          Tous les éléments de la plateforme Kurso (interface, logo, code, algorithmes) sont protégés par le droit de la propriété intellectuelle et appartiennent à Kurso SAS.
        </p>
        <p className="mt-2">
          Les cours générés par l'IA à votre demande vous sont accordés sous licence d'usage personnel, non exclusive. Vous ne pouvez pas les revendre, les redistribuer ou les exploiter commercialement sans autorisation écrite de Kurso.
        </p>
      </LegalSection>

      <LegalSection title="Article 9 — Comportement de l'utilisateur">
        <p>Il est interdit de :</p>
        <ul className="list-disc pl-5 space-y-1 text-[#a0a0ab] mt-2">
          <li>Utiliser Kurso à des fins illégales ou contraires à l'ordre public</li>
          <li>Générer des contenus à caractère haineux, discriminatoire ou illicite</li>
          <li>Tenter de contourner les limites techniques ou de sécurité</li>
          <li>Utiliser des bots ou systèmes automatisés non autorisés</li>
          <li>Usurper l'identité d'un autre utilisateur</li>
        </ul>
      </LegalSection>

      <LegalSection title="Article 9 bis — Usage des contenus générés par l'IA et protection contre les abus">
        <p>
          Les cours générés sur Kurso via l'intelligence artificielle sont destinés à un <strong>usage personnel d'apprentissage</strong>. Toute utilisation commerciale — notamment la revente, la redistribution à des tiers payants, ou l'utilisation dans le cadre d'une activité de formation professionnelle rémunérée sans accord préalable de Kurso — est <strong>strictement interdite</strong>.
        </p>
        <p className="mt-2">
          Les formateurs et organismes de formation souhaitant utiliser Kurso à des fins professionnelles sont invités à souscrire au <strong>plan Créateur Pro (29 €/mois)</strong> ou au plan Organisation, lesquels autorisent expressément l'usage dans un cadre professionnel.
        </p>
        <p className="mt-2">
          Kurso dispose de systèmes de détection automatique des comportements abusifs (volume de génération excessif, taux d'export anormal, ratio génération/consultation). Tout utilisateur dont le comportement est signalé comme suspect pourra recevoir un avertissement et, en cas de récidive, voir son compte suspendu ou résilié <strong>sans remboursement</strong> conformément aux présentes CGU.
        </p>
        <p className="mt-2">
          Les exports PDF et PowerPoint produits sur les plans Solo et Famille comportent un <strong>filigrane obligatoire</strong> incluant l'adresse email de l'utilisateur et un identifiant d'export unique. Ce dispositif permet de tracer l'origine de tout contenu redistribué.
        </p>
      </LegalSection>

      <LegalSection title="Article 10 bis — Responsabilité des mentors">
        <p>
          Les mentors présents sur Kurso sont des facilitateurs bénévoles ou indépendants. Leurs conseils ne remplacent en aucun cas l'avis d'un professionnel qualifié (médecin, juriste, psychologue, thérapeute, comptable, conseiller financier, ou tout autre expert certifié dans son domaine).
        </p>
        <p className="mt-2">
          Kurso agit uniquement comme plateforme de mise en relation et décline toute responsabilité quant aux conseils prodigués par les mentors. L'utilisateur est seul juge de la pertinence des conseils reçus et de leur mise en application. Kurso ne vérifie pas les qualifications professionnelles des mentors et ne garantit pas l'exactitude, l'exhaustivité ou l'adéquation des conseils prodigués à la situation personnelle de l'utilisateur.
        </p>
        <p className="mt-2">
          En cas de litige entre un mentor et un utilisateur, Kurso ne peut être tenu responsable et invite les parties à résoudre leur différend à l'amiable. Kurso se réserve le droit de suspendre ou retirer tout mentor dont le comportement serait signalé comme contraire aux présentes CGU ou à la Charte Mentor disponible sur <a href="/charte-mentor" className="text-[#ff6b6b] hover:underline">kurso.fr/charte-mentor</a>.
        </p>
      </LegalSection>

      <LegalSection title="Article 11 — Responsabilité générale de la plateforme">
        <p>
          Kurso met tout en œuvre pour assurer la disponibilité de la plateforme (SLA indicatif : 99,5 %). Cependant, Kurso ne peut être tenu responsable des interruptions de service liées à des cas de force majeure, maintenances nécessaires ou défaillances de sous-traitants.
        </p>
        <p className="mt-2">
          La responsabilité de Kurso est limitée au montant des sommes effectivement versées par l'utilisateur au cours des 12 derniers mois.
        </p>
      </LegalSection>

      <LegalSection title="Article 12 — Droit applicable et juridiction">
        <p>
          Les présentes CGU sont soumises au droit français. Tout litige sera soumis, à défaut de résolution amiable, à la compétence des tribunaux du ressort du siège social de Kurso SAS.
        </p>
        <p className="mt-2">
          En cas de litige de consommation, vous pouvez recourir gratuitement à un médiateur de la consommation via la plateforme européenne : <strong>ec.europa.eu/consumers/odr</strong>.
        </p>
      </LegalSection>

      <LegalSection title="Article 13 — Contact">
        <p>
          Pour toute question relative aux présentes CGU :{' '}
          <a href="mailto:contact@mykurso.fr" className="text-[#ff6b6b] hover:underline">contact@mykurso.fr</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
